/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This Page corresponds to the Admin authentication
 * allowing the user to sign in as an administrator
 * and manage cabinet's configuration
 */

// Imports
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

// Utils
import {GlobalApiCall, HTTPResponse } from '../../utils/ApiCallConstructor';

// Buroclic adaptive components
import { PoweredByBc } from '../../buroclic/PoweredByBc';

export const AdminLoginPage: React.FC = () => {

  document.title = 'Espace client | Admin';

  // form fields
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState(``);
  const [isPwdShown, setIsPwdShown] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  // Defines the code to be executed when the form is submit
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await signInAsAdmin();
  };

  useEffect(() => {
    setErrorMessage('');
  }, [login, password]);

  /**
   * Tries to connect the user through the Back-End
   * using credentials he/she just gave us
   */
  const signInAsAdmin = async () => {

    // Check for form fields
    if (!login || !password) {
      setErrorMessage('Veuillez renseigner l\'intégralité des éléments de connexion Administrateur');
      return;
    }

    else {
      try {
        await toast.promise(
          GlobalApiCall({
            endpoint: '/account/signin',
            method: 'POST',
            body: {
              login: login,
              password: password
            }
          }),
          {
            pending: 'Connexion en cours',
            error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } }
          });

        // Connected successfully
        window.localStorage.setItem('isAdminAuthenticated', "true");
        navigate('/admin');

      } catch (err: any) { console.log(err); console.error(err.error) }
    }
  };

  // HTML Template
  return (
    <div className="h-full flex items-center justify-center bg-cover bg-no-repeat" style={{ backgroundImage: `url('/images/adminBackground.png')` }}>
      <div className="w-4/5 justify-center flex flex-col bg-white rounded-lg lg:w-1/3 min-h-[40%] h-[40%] lg:mr-auto lg:ml-12 lg:h-[min-content] lg:my-auto">
        <img src={`/images/buroclic.png`} alt="Company logo" className="h-auto w-full object-contain object-center max-h-[16rem] my-4 mx-auto max-w-[16rem]" />
        <span className='text-center font-bold text-red-500 text-4xl'>Pannel d'Administration</span>

        <form onSubmit={handleFormSubmit} className="border border-black rounded-md p-12 sm:m-3 lg:m-12 pb-0 px-3 pt-2 sm:h-4/5 lg:h-2/3 grow" style={{ width: '-webkit-fill-available' }}>
          <div className='flex flex-col items-start grow' style={{ height: 'calc(100% - 2rem)' }}>
            <img src="/images/connexionIcon.png" alt="User icon" className="mx-auto mb-2 lg:w-1/5 w-10" />

            {/* Login */}
            <label htmlFor="login" className="text-gray-500 font-bold hidden lg:inline-block">Login :</label>
            <input type="text" id="login" placeholder='Login' value={login} onChange={(e) => setLogin(e.target.value)}
              className="border border-gray-300 bg-white py-2 px-2 rounded-md h-10 mb-3 shadow-bottom" style={{ width: '-webkit-fill-available' }} />

            {/* Password */}
            <label htmlFor="password" className="text-gray-500 font-bold hidden lg:inline-block">Mot de passe :</label>
            <div className="border mb-1 lg:mb-3 flex items-center rounded-md w-full focus-within:ring-2 focus-within:ring-black">
              <input type={`${isPwdShown ? 'text' : 'password'}`} id="password" placeholder='Mot de passe' value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-white py-2 px-2 rounded-md h-10 shadow-bottom outline-none w-[inherit]" />
              <button type='button' onClick={() => setIsPwdShown(!isPwdShown)} className="border-l border-l-gray-300 h-10 w-[4rem]">
                <FontAwesomeIcon className={`h-7 w-7 mx-3`} icon={isPwdShown ? faEyeSlash : faEye} />
              </button>
            </div>

            {/* Error */}
            {errorMessage && <p className="text-red-500 mx-auto font-bold ">{errorMessage}</p>}

            {/* Submit */}
            <button className="bg-[#E7812B] text-white text-1xl font-bold py-2 px-12 w-52 mt-3 mb-4 rounded-lg shadow-lg mx-auto" type="submit">
              Connexion
            </button>
          </div>

          <PoweredByBc className={'h-8 py-1 mx-auto'} imgClassName={'h-full w-auto m-auto mb-8'}/>

        </form>
      </div>
    </div>
  );
};